import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { Platform } from 'react-native';

/**
 * Camera/mic join flags carried across the pre-flight → exam room remount.
 *
 * {@link MeetingSessionShell} keys {@link MeetingRoom} by `roomName`, so the
 * per-booking exam room reconnect applies `joinSettings` again via
 * `handleConnected`. This context records a successful grant. The live session
 * publishes the camera once granted, and publishes the microphone only outside
 * the shared pre-flight and group-briefing rooms.
 */

export type CarriedJoinSettings = {
  /** True once device grant succeeded or reload found both permissions granted. */
  mediaGranted: boolean;
  /** Call after the camera check has permission and has published what this room allows. */
  markMediaGranted: () => void;
};

const CarriedJoinSettingsContext = createContext<CarriedJoinSettings>({
  mediaGranted: false,
  markMediaGranted: () => {},
});

async function queryBothPermissionsGranted(): Promise<boolean> {
  if (Platform.OS !== 'web') return false;
  const permissions = (globalThis as {
    navigator?: { permissions?: { query: (desc: { name: string }) => Promise<{ state: string }> } };
  }).navigator?.permissions;
  if (!permissions?.query) return false;
  try {
    const [camera, microphone] = await Promise.all([
      permissions.query({ name: 'camera' }),
      permissions.query({ name: 'microphone' }),
    ]);
    return camera.state === 'granted' && microphone.state === 'granted';
  } catch {
    return false;
  }
}

export function CarriedJoinSettingsProvider({ children }: { children: React.ReactNode }) {
  const [mediaGranted, setMediaGranted] = useState(false);

  useEffect(() => {
    queryBothPermissionsGranted()
      .then((granted) => {
        if (granted) setMediaGranted(true);
      })
      .catch(() => undefined);
  }, []);

  const markMediaGranted = useCallback(() => {
    setMediaGranted(true);
  }, []);

  const value = useMemo<CarriedJoinSettings>(
    () => ({ mediaGranted, markMediaGranted }),
    [mediaGranted, markMediaGranted],
  );

  return (
    <CarriedJoinSettingsContext.Provider value={value}>
      {children}
    </CarriedJoinSettingsContext.Provider>
  );
}

export function useCarriedJoinSettings(): CarriedJoinSettings {
  return useContext(CarriedJoinSettingsContext);
}
